import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Select, { StylesConfig } from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import {
  bulkAssignBPAFindingWatchers,
  bulkRemoveBPAFindingWatchers,
  fetchBPAFindingWatchers,
  fetchBPAWatcherOptions,
} from '../../services/bpaFindingsService';
import { findingsDataRefreshAtom } from '../../state';
import {
  BPAAssigneeOption,
  BPAWatcher,
  BPAWatcherMutationPayload,
} from '../../types/bpaFindings.types';

interface BPAWatcherModalProps {
  onClose: () => void;
  findingIds: number[];
  isBulk?: boolean;
  summaryCard?: React.ReactNode;
}

const getMemberName = (member: BPAAssigneeOption): string => {
  const fullName =
    `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim();
  return fullName || member.username || member.email;
};

const getMemberLabel = (member: BPAAssigneeOption): string =>
  `${getMemberName(member)} (${member.email})`;

const selectStyles: StylesConfig<BPAAssigneeOption, true> = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    maxHeight: '120px',
    cursor: 'pointer',
  }),
  option: (base) => ({ ...base, cursor: 'pointer' }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: '120px',
    overflow: 'auto',
    flexWrap: 'wrap',
  }),
  multiValue: (base) => ({ ...base, margin: '2px' }),
  menuList: (base) => ({
    ...base,
    maxHeight: '230px',
    overflowY: 'auto',
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const BPAWatcherModal: React.FC<BPAWatcherModalProps> = ({
  onClose,
  findingIds,
  isBulk = false,
  summaryCard,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);
  const findingIdsKey = useMemo(() => findingIds.join(','), [findingIds]);

  const [loading, setLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);
  const [members, setMembers] = useState<BPAAssigneeOption[]>([]);
  const [currentWatchers, setCurrentWatchers] = useState<BPAAssigneeOption[]>(
    [],
  );
  const [existingWatchers, setExistingWatchers] = useState<BPAWatcher[]>([]);
  const [selectedWatcherRowIds, setSelectedWatcherRowIds] = useState<number[]>(
    [],
  );

  const fetchAllFindingWatchers = useCallback(
    async (isCancelled: () => boolean): Promise<BPAWatcher[]> => {
      const allWatchers: BPAWatcher[] = [];
      let page = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const watchersResponse = await fetchBPAFindingWatchers({
          company_id: companyId,
          finding_ids: findingIdsKey,
          page,
          page_size: 100,
        });

        if (isCancelled()) return allWatchers;

        allWatchers.push(...(watchersResponse.results ?? []));
        hasNextPage = Boolean(watchersResponse.next);
        page += 1;
      }

      return allWatchers;
    },
    [companyId, findingIdsKey],
  );

  useEffect(() => {
    if (!companyId || companyId === -1) return;

    let cancelled = false;
    const isCancelled = () => cancelled;

    const fetchData = async () => {
      setLoading(true);

      try {
        const fetchedMembers = await fetchBPAWatcherOptions(companyId);
        if (isCancelled()) return;

        setMembers(fetchedMembers);

        if (!isBulk && findingIdsKey) {
          const allWatchers = await fetchAllFindingWatchers(isCancelled);
          if (!isCancelled()) {
            setExistingWatchers(allWatchers);
          }
        }
      } catch (err) {
        if (!isCancelled()) {
          showFailureToast(
            extractApiErrorMessage(err, 'Failed to load watcher data.'),
          );
        }
      } finally {
        if (!isCancelled()) {
          setLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [companyId, fetchAllFindingWatchers, findingIdsKey, isBulk]);

  const availableWatcherOptions = useMemo(() => {
    if (isBulk) return members;

    const existingWatcherIds = new Set(
      existingWatchers.map((watcher) => watcher.watcher_user_id),
    );
    return members.filter((member) => !existingWatcherIds.has(member.users_id));
  }, [existingWatchers, isBulk, members]);

  const getWatcherGridConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      {
        key: 'name',
        label: 'Name',
        overrideContents: (rowData: BPAWatcher) => rowData.watcher_name,
      },
      {
        key: 'email',
        label: 'Email',
        overrideContents: (rowData: BPAWatcher) => rowData.watcher_email,
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (rowData: BPAWatcher) => (
          <IconButton
            id={`bpa-remove-watcher-${rowData.id}`}
            iconClass="fa fa-user-times"
            size="sm"
            tooltip="Remove watcher"
            onClick={() => void handleSave('remove', rowData.watcher_user_id)}
          />
        ),
        headerCellStyle: {
          textAlign: 'right',
          maxWidth: 'auto',
          minWidth: '150px',
        },
        rowCellStyle: {
          textAlign: 'right',
          minWidth: '150px',
          maxWidth: 'auto',
          whiteSpace: 'nowrap',
        },
      },
    ],
    columnsDisplayConfig: {
      enabledColumnKeys: ['name', 'email', 'actions'],
    },
    rowSelectionConfig: {
      preSelectedRows: selectedWatcherRowIds,
      onSelectionChange: setSelectedWatcherRowIds,
    },
  });

  const getWatcherIdsForAction = (
    action: 'assign' | 'remove' | 'removeAll',
    watcherId?: number,
  ): number[] => {
    if (action === 'assign') {
      return currentWatchers.map((watcher) => watcher.users_id);
    }

    if (isBulk && action === 'removeAll') {
      return [];
    }

    if (isBulk) {
      return currentWatchers.map((watcher) => watcher.users_id);
    }

    if (watcherId) return [watcherId];

    return existingWatchers
      .filter((watcher) => selectedWatcherRowIds.includes(watcher.id))
      .map((watcher) => watcher.watcher_user_id);
  };

  const updateRowWatcherState = useCallback(
    (action: 'assign' | 'remove' | 'removeAll', watcherIds: number[]) => {
      if (isBulk) return;

      if (action === 'assign') {
        const existingWatcherIds = new Set(
          existingWatchers.map((watcher) => watcher.watcher_user_id),
        );
        const newWatchers = currentWatchers
          .filter((watcher) => !existingWatcherIds.has(watcher.users_id))
          .map((watcher) => ({
            id: -Math.abs(Date.now() + watcher.users_id),
            finding_id: findingIds[0],
            watcher_user_id: watcher.users_id,
            watcher_name: getMemberName(watcher),
            watcher_email: watcher.email,
            assigned_by_user_id: 0,
            assigned_by_name: '',
            assigned_by_email: '',
            assigned_date: new Date().toISOString(),
          }));

        setExistingWatchers((prev) => [...prev, ...newWatchers]);
        return;
      }

      setExistingWatchers((prev) =>
        prev.filter((watcher) => !watcherIds.includes(watcher.watcher_user_id)),
      );
    },
    [currentWatchers, existingWatchers, findingIds, isBulk],
  );

  const handleSave = async (
    action: 'assign' | 'remove' | 'removeAll',
    watcherId?: number,
  ) => {
    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before managing watchers.');
      return;
    }

    if (findingIds.length === 0) {
      showFailureToast('No BPA findings selected.');
      return;
    }

    const watcherIds = getWatcherIdsForAction(action, watcherId);
    if (action !== 'removeAll' && watcherIds.length === 0) {
      showFailureToast('No watchers selected.');
      return;
    }

    if (action === 'assign') {
      setIsAssigning(true);
    } else if (action === 'removeAll') {
      setIsRemovingAll(true);
    } else {
      setIsRemoving(true);
    }

    try {
      const payload: BPAWatcherMutationPayload = {
        company_id: companyId,
        finding_ids: findingIds,
      };
      if (action === 'removeAll') {
        payload.remove_all = true;
      } else {
        payload.watcher_ids = watcherIds;
      }

      const response =
        action === 'assign'
          ? await bulkAssignBPAFindingWatchers(payload)
          : await bulkRemoveBPAFindingWatchers(payload);

      if (response.status === 200 || response.status === 201) {
        const actionText = action === 'assign' ? 'assigned' : 'unassigned';
        showSuccessToast(`Watchers successfully ${actionText}.`);
        updateRowWatcherState(action, watcherIds);
        refreshFindingsGridData(new Date().toISOString());
        setCurrentWatchers([]);
        setSelectedWatcherRowIds([]);
      } else {
        throw new Error(
          `Failed to ${action === 'assign' ? 'assign' : 'unassign'} watchers.`,
        );
      }
    } catch (err) {
      showFailureToast(
        extractApiErrorMessage(
          err,
          `Failed to ${action === 'assign' ? 'assign' : 'unassign'} watchers.`,
        ),
      );
    } finally {
      setIsAssigning(false);
      setIsRemoving(false);
      setIsRemovingAll(false);
    }
  };

  const removeButton = () => (
    <Button
      color="secondary"
      onClick={() => void handleSave('remove')}
      disabled={
        isAssigning ||
        isRemoving ||
        (isBulk && currentWatchers.length === 0) ||
        (!isBulk && selectedWatcherRowIds.length === 0)
      }
      className="d-flex align-items-center"
      style={{ marginRight: '10px' }}
    >
      {isRemoving ? (
        <>
          <CircleSpinner size={20} color="#fff" loading={true} />
          <span className="ml-2">Unassigning Watchers...</span>
        </>
      ) : isBulk && currentWatchers.length > 0 ? (
        `Unassign ${currentWatchers.length} ${
          currentWatchers.length === 1 ? 'Watcher' : 'Watchers'
        }`
      ) : !isBulk && selectedWatcherRowIds.length > 0 ? (
        `Unassign ${selectedWatcherRowIds.length} ${
          selectedWatcherRowIds.length === 1 ? 'Watcher' : 'Watchers'
        }`
      ) : (
        'Unassign Watchers'
      )}
    </Button>
  );

  return (
    <ModalDialog
      style={{ maxWidth: '800px' }}
      size="lg"
      title={`Manage${
        findingIds.length === 1 ? ' Finding' : ' Findings'
      } Watchers`}
      subTitle={
        <p className="d-flex align-items-center">
          {isBulk && (
            <span>
              <strong>{findingIds.length}</strong>{' '}
              {findingIds.length === 1 ? 'Finding' : 'Findings'} selected for
              watcher assignment
            </span>
          )}
        </p>
      }
      childComponent={
        <Row>
          <Col>
            <Card className="p-2">
              <CardBody>
                {isBulk && (
                  <p>
                    Select watchers for the selected findings. Add/remove
                    actions are applied to every selected finding.
                    <br />
                    <br />
                    Existing watcher pairs are skipped by the backend when
                    adding, and missing watcher pairs are skipped when removing.
                  </p>
                )}

                {!isBulk && (
                  <div className="mb-2">
                    {summaryCard}
                    <h5 className="mb-2">
                      <strong>Current Watchers</strong>
                    </h5>
                    <Suspense fallback={<LoadingScreen />}>
                      <div
                        style={{
                          minHeight: loading ? '120px' : undefined,
                          maxHeight: '260px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                        }}
                      >
                        <DataGrid
                          config={getWatcherGridConfig()}
                          data={existingWatchers}
                          loading={loading}
                        />
                      </div>
                      {existingWatchers.length > 0 && (
                        <div className="d-flex justify-content-end mt-1">
                          {removeButton()}
                        </div>
                      )}
                    </Suspense>
                  </div>
                )}

                <div className={isBulk ? 'mt-2' : 'mt-3'}>
                  <h5 className="mb-3">
                    <strong>
                      {isBulk ? 'Add/Remove Watchers' : 'Add New Watchers'}
                    </strong>
                  </h5>
                  <p className="text-muted mb-3">
                    {isBulk
                      ? 'Select members to add/remove as watchers.'
                      : 'Select members to add as watchers. Current watchers are not shown in the list.'}
                  </p>
                  <Select
                    className="custom-360-dropdown profile-pic-dropdown"
                    closeMenuOnSelect={false}
                    isMulti
                    isClearable
                    hideSelectedOptions
                    onChange={(selected) =>
                      setCurrentWatchers([...selected] as BPAAssigneeOption[])
                    }
                    value={currentWatchers}
                    options={availableWatcherOptions}
                    getOptionLabel={getMemberLabel}
                    getOptionValue={(option) => option.users_id.toString()}
                    isDisabled={loading || isAssigning || isRemoving}
                    placeholder={
                      loading
                        ? 'Loading members...'
                        : 'Select members to add as watchers...'
                    }
                    styles={selectStyles}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      }
      customFooter={
        <div className="d-flex justify-content-between w-100">
          <Button
            color="secondary"
            onClick={onClose}
            disabled={isAssigning || isRemoving || isRemovingAll}
          >
            Cancel
          </Button>
          <div className="d-flex align-items-center">
            {isBulk && (
              <Button
                color="danger"
                onClick={() => void handleSave('removeAll')}
                disabled={loading || isAssigning || isRemoving || isRemovingAll}
                className="d-flex align-items-center"
                style={{ marginRight: '10px' }}
              >
                {isRemovingAll ? (
                  <>
                    <CircleSpinner size={20} color="#fff" loading={true} />
                    <span className="ml-2">Unassigning All Watchers...</span>
                  </>
                ) : (
                  'Unassign All Watchers'
                )}
              </Button>
            )}
            {isBulk && removeButton()}
            <Button
              color="primary"
              onClick={() => void handleSave('assign')}
              disabled={
                currentWatchers.length === 0 ||
                isAssigning ||
                isRemoving ||
                isRemovingAll
              }
              className="d-flex align-items-center"
            >
              {isAssigning ? (
                <>
                  <CircleSpinner size={20} color="#fff" loading={true} />
                  <span className="ml-2">Assigning Watchers...</span>
                </>
              ) : currentWatchers.length > 0 ? (
                `Assign ${currentWatchers.length} ${
                  currentWatchers.length === 1 ? 'Watcher' : 'Watchers'
                }`
              ) : (
                'Assign Watchers'
              )}
            </Button>
          </div>
        </div>
      }
      onClose={onClose}
    />
  );
};

export default BPAWatcherModal;
